import * as React from 'react';
import {useRef, useState} from 'react';
import {KatDropdown, KatInput, KatLink, KatTextarea} from '@amzn/katal-react';
import {FieldData} from 'src/app/model/lighthouse/Common';
import {FieldType} from 'src/app/constant/Enums';

export interface FieldRendererProps {
    field: FieldData;
    onChange?: (label: string, value: string) => void;
    readOnly?: boolean;
}

/**
 * Renders a single FieldData item based on its type.
 * Supports: text, link, radio, dropdown, number, date, textarea, freeform.
 * Handles both read-only display and editable inputs using Katal components.
 */
const FieldRenderer: React.FC<FieldRendererProps> = ({field, onChange, readOnly}) => {
    const inputRef = useRef<any>(null);
    const isEditable = field.editable && !readOnly;

    const handleChange = (value: string) => {
        if (onChange) {
            onChange(field.label, value);
        }
    };

    const requiredMark = field.required ? <span className="lighthouse-required">*</span> : null;

    switch (field.type) {
        case FieldType.TEXT:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-text-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatInput
                            value={field.value || ''}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-text-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.LINK:
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-link-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <KatLink href={field.href || '#'} target="_blank" label={field.value || field.href || '—'} />
                    {field.trailingText && <span className="lighthouse-field-value">{field.trailingText}</span>}
                </div>
            );

        case FieldType.RADIO:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-radio-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <span className="lighthouse-radio-options">
                            {(field.options || []).map(opt => (
                                <label key={opt} className="lighthouse-radio-option">
                                    <input
                                        type="radio"
                                        name={`radio-${field.label}`}
                                        value={opt}
                                        checked={field.value === opt}
                                        onChange={() => handleChange(opt)}
                                    />
                                    {opt}
                                </label>
                            ))}
                        </span>
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-radio-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.DROPDOWN:
            if (isEditable) {
                const dropdownOptions = (field.options || []).map(opt => ({
                    name: opt,
                    value: opt
                }));
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-dropdown-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatDropdown
                            placeholder="Select..."
                            options={dropdownOptions}
                            value={field.value || undefined}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-dropdown-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.NUMBER:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-number-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatInput
                            type="number"
                            value={field.value || ''}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-number-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.DATE:
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-date-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">
                        {field.value ? new Date(field.value).toLocaleDateString() : '—'}
                    </span>
                </div>
            );

        case FieldType.TEXTAREA:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-textarea-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatTextarea
                            value={field.value || ''}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-textarea-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.FREEFORM:
            return <FreeformField field={field} onChange={handleChange} isEditable={!!isEditable} requiredMark={requiredMark} />;

        default:
            return (
                <div className="lighthouse-field-readonly">
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || 'N/A'}</span>
                </div>
            );
    }
};

/**
 * FreeformField — Inline field with "Add" link that opens a popup textarea.
 * Click tick (✓) to save and close popup.
 */
const FreeformField: React.FC<{
    field: FieldData;
    onChange: (value: string) => void;
    isEditable: boolean;
    requiredMark: React.ReactNode;
}> = ({field, onChange, isEditable, requiredMark}) => {
    const [showPopup, setShowPopup] = useState(false);
    const [text, setText] = useState(field.value || '');
    const [savedValue, setSavedValue] = useState(field.value || '');

    const handleSave = () => {
        setSavedValue(text);
        onChange(text);
        setShowPopup(false);
    };

    // Show only first 5 words as preview
    const getPreview = (val: string) => {
        if (!val) return '';
        const words = val.split(/\s+/);
        if (words.length <= 5) return val;
        return words.slice(0, 5).join(' ') + '...';
    };

    return (
        <div className="lighthouse-field-inline" data-testid={`field-freeform-${field.label}`}>
            <span className="lighthouse-field-label">{field.label}{requiredMark}:</span>
            <span className="lighthouse-field-value">{getPreview(savedValue)}</span>
            {isEditable && (
                <button
                    className="lighthouse-edit-link"
                    onClick={() => setShowPopup(true)}
                    aria-label={`Add ${field.label}`}
                >
                    Add
                </button>
            )}
            {showPopup && (
                <div className="lighthouse-freeform-popup">
                    <textarea
                        className="lighthouse-freeform-textarea"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Enter text..."
                        autoFocus
                    />
                    <button className="lighthouse-freeform-save" onClick={handleSave} title="Save">✓</button>
                </div>
            )}
        </div>
    );
};

export default FieldRenderer;
